import React, { useCallback, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useSession, useStatement, useToast } from "../../hooks";
import { resendDistributionEmail } from "../../services";
import { Recipient, Statement, UpdateRecipient } from "../../dtos";
import { Button, Checkbox, ImageHandler, ShareModal } from "../../components";
import { MainLayout } from "../../components/layouts";
import { DotsIcon, VoucherIcon } from "../../components/icons";
import { Container, DistributionStatus, TotalAmount } from "./styles";
import { DistributionStatusEnum } from "../../enums/DistributionStatus";
import { RecipientModal } from "../DistributionPage/components";

const DistributionDetailPage: React.FC = () => {
  const [showSelection, setShowSelection] = useState<boolean>(false);
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(
    null
  );
  const [editModalRecipient, setEditModalRecipient] =
    useState<UpdateRecipient | null>(null);
  const [selectedRecipients, setSelectedRecipients] = useState<Recipient[]>([]);
  const { state } = useLocation() as {
    state: {
      statement?: Statement;
    };
  };

  const { statements, setStatements } = useStatement();
  const { handleRequest } = useSession();
  const { showToast } = useToast();

  const locationStatement = state?.statement;

  const statement = locationStatement
    ? statements.find((s) => s.id === locationStatement.id) || locationStatement
    : undefined;

  const getFormattedDate = useCallback(() => {
    if (!statement) return "";

    let formatted = format(
      statement.date as Date,
      `EEEE, dd 'de' LLLL 'de' yyyy`,
      {
        locale: ptBR,
      }
    );
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);

    return formatted;
  }, [statement]);

  const getFormattedHours = useCallback(() => {
    if (!statement) return "";

    return format(statement.date as Date, "HH:mm", {
      locale: ptBR,
    });
  }, [statement]);

  const getTotalAmount = useCallback(() => {
    if (!statement) return 0;

    return statement.recipients.reduce(
      (amount, recipient) => amount + recipient.amount,
      0
    );
  }, [statement]);

  const getRecipientStatus = (recipient: Recipient): DistributionStatusEnum => {
    if (recipient.voucherBatchStatus === "INACTIVE")
      return DistributionStatusEnum.Blocked;
    if (recipient.redeemedDate) return DistributionStatusEnum.Captured;
    if (recipient.canceledDate) return DistributionStatusEnum.Expired;
    return DistributionStatusEnum.Distributed;
  };

  const handleSendEmail = useCallback(async () => {
    const recipientsId = selectedRecipients.map((recipient) => recipient.id);

    const response = await handleRequest(async () => {
      await resendDistributionEmail(recipientsId);
    });

    if (response.success) {
      setShowSelection(false);
      showToast({
        type: "success",
        message: "E-mail enviado com sucesso",
      });
    }
  }, [showToast, handleRequest, selectedRecipients]);

  if (!statement) return <Navigate to="/home" replace />;

  const renderShareModal = () => {
    if (!selectedRecipient) return null;

    return (
      <ShareModal
        selectedRecipient={selectedRecipient}
        showSelection={showSelection}
        handleClose={() => setSelectedRecipient(null)}
        options={["whatsapp", "native-share"]}
        allowEdit
        onOpenEdit={() => {
          setEditModalRecipient(parseRecipientToUpdate(selectedRecipient));
        }}
      />
    );
  };

  const parseRecipientToUpdate = (recipient: Recipient): UpdateRecipient => {
    return { ...recipient, distributionId: statement.id };
  };

  const handleRecipientUpdated = (updatedRecipient: Recipient) => {
    setStatements((prevStatements) =>
      prevStatements.map((st) => {
        if (st.id !== statement.id) return st;

        const updatedRecipients = st.recipients.map((rec) =>
          rec.id === updatedRecipient.id ? updatedRecipient : rec
        );

        return { ...st, recipients: updatedRecipients };
      })
    );

    if (selectedRecipient?.id === updatedRecipient.id)
      setSelectedRecipient(updatedRecipient);
  };

  return (
    <MainLayout
      id="distribution.detail"
      title="Detalhe da distribuição"
      showBackButton
      showCloseButton={false}
      showFloatingNavigationMenu={false}
    >
      <Container $showSelection={showSelection}>
        <span>{getFormattedDate()}</span>
        <span>{getFormattedHours()}</span>
        <ImageHandler
          url={statement.exhibitor.logo}
          alt={statement.exhibitor.name}
        />
        <strong>{statement.exhibitor.name}</strong>
        <small>{statement.voucherType.name}</small>
        <TotalAmount>
          <VoucherIcon />
          <span>{getTotalAmount()}</span>
        </TotalAmount>
        <ul>
          {statement.recipients.map((recipient, index) => (
            <li key={index}>
              <div>
                <span>{recipient.amount}</span>
                {showSelection && (
                  <Checkbox
                    onChange={(e) => {
                      if (e.currentTarget.checked) {
                        setSelectedRecipients((oldRecipients) => [
                          ...oldRecipients,
                          recipient,
                        ]);
                      } else {
                        setSelectedRecipients((oldRecipients) =>
                          oldRecipients.filter(
                            (oldRecipient) =>
                              oldRecipient.email !== recipient.email
                          )
                        );
                      }
                    }}
                  />
                )}
              </div>
              <div>
                <small>{recipient.email}</small>
                <small>{recipient.name}</small>
                <small>{recipient.phoneNumber || "Número não informado"}</small>
              </div>
              <div>
                <DistributionStatus $status={getRecipientStatus(recipient)}>
                  {getRecipientStatus(recipient)}
                </DistributionStatus>
                <DotsIcon onClick={() => setSelectedRecipient(recipient)} />
              </div>
            </li>
          ))}
        </ul>
        {showSelection ? (
          <>
            <Button
              id="distribution.detail.sendemailbutton"
              disabled={selectedRecipients.length < 1}
              onClick={handleSendEmail}
            >
              Enviar
            </Button>
            <Button
              id="distribution.detail.cancelemailbutton"
              primaryColor={false}
              onClick={() => setShowSelection(false)}
            >
              Cancelar
            </Button>
          </>
        ) : (
          <Button
            id="distribution.detail.showselectionbutton"
            onClick={() => setShowSelection(true)}
          >
            Reenviar e-mail
          </Button>
        )}
      </Container>
      {renderShareModal()}
      {editModalRecipient && (
        <RecipientModal
          title="Detalhe do destinatário"
          modalType="update"
          recipientData={{
            recipient: editModalRecipient,
            index: editModalRecipient.id,
          }}
          handleClose={() => setEditModalRecipient(null)}
          handleSave={(recipient) => handleRecipientUpdated(recipient)}
        />
      )}
    </MainLayout>
  );
};

export { DistributionDetailPage };
